import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ContactModal from './ContactModal';
import CategoriesModal from './CategoriesModal';
import SearchModal from './SearchModal';
import { useBusinessInfo } from '../hooks/useBusinessInfo';
import { CartContext } from '../contexts/CartContext';
import AsportoCart from './AsportoCart';
import TableCart from './TableCart';
import './SharedFooter.css';

const SharedFooter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const { businessInfo, loading: businessLoading } = useBusinessInfo();

  // CartContext opzionale — disponibile solo nelle pagine pubbliche
  const cart = useContext(CartContext);
  const asportoEnabled = cart?.asportoEnabled ?? false;
  const asportoMode = cart?.asportoMode ?? false;
  const cartCount = cart?.cartCount ?? 0;
  const cartOpen = cart?.cartOpen ?? false;
  const setAsportoMode = cart?.setAsportoMode;
  const setCartOpen = cart?.setCartOpen;
  const cartItems = cart?.items ?? [];
  const updateCartQty = cart?.updateQty;
  const whatsappNumber = cart?.whatsappNumber ?? '';
  const businessName = cart?.businessName ?? '';
  const tableUuid = cart?.tableUuid ?? null;
  const tableLabel = cart?.tableLabel ?? '';
  const clearCart = cart?.clearCart;

  const [showToast, setShowToast] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevAsportoMode = useRef(false);

  useEffect(() => {
    if (asportoMode && !prevAsportoMode.current) {
      // Asporto appena attivato — mostra il toast
      setShowToast(true);
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setShowToast(false), 2800);
    }
    prevAsportoMode.current = asportoMode;
    return () => { if (toastTimer.current) clearTimeout(toastTimer.current); };
  }, [asportoMode]);

  const handleAsportoClick = () => {
    if (!cart) return;
    if (!asportoMode) {
      setAsportoMode!(true);
    } else {
      setCartOpen!(true);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Menu del Ristorante',
      text: 'Scopri il nostro menu!',
      url: window.location.origin + '/menu'
    };

    try {
      // Prova a usare l'API Web Share se disponibile (mobile)
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copia il link negli appunti
        await navigator.clipboard.writeText(shareData.url);
        alert('Link del menu copiato negli appunti!');
      }
    } catch (error) {
      console.error('Errore nella condivisione:', error);
      // Fallback finale: mostra il link in un alert
      alert(`Condividi questo link: ${shareData.url}`);
    }
  };

  const handleCategoriesClick = () => {
    setIsCategoriesModalOpen(true);
  };

  const handleMenuClick = () => {
    // Se siamo già nel menu, scorri verso l'alto per vedere le categorie
    if (location.pathname === '/menu') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/menu');
    }
  };

  const handleSearchClick = () => {
    setIsSearchModalOpen(true);
  };

  const handleContactClick = () => {
    setIsContactModalOpen(true);
  };

  return (
    <>
      {/* Toast "Asporto attivo" */}
      {showToast && (
        <div className="asporto-toast">
          <span className="asporto-toast-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Asporto attivo
          </span>
          <span className="asporto-toast-sub">Vai a scegliere i tuoi piatti e aggiungili al carrello!</span>
        </div>
      )}

      <footer className="shared-footer">
        <nav className="footer-nav">
          <button onClick={handleMenuClick} className="footer-nav-item footer-nav-button">
            <svg className="footer-nav-icon" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <line x1="9" y1="9" x2="15" y2="9"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            Menu
          </button>


          <button onClick={handleContactClick} className="footer-nav-item footer-nav-button">
            <svg className="footer-nav-icon" viewBox="0 0 24 24">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Contatti
          </button>
          <button onClick={handleShare} className="footer-nav-item footer-nav-button">
            <svg className="footer-nav-icon" viewBox="0 0 24 24">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            Condividi
          </button>
          {tableUuid ? (
            <button
              onClick={() => setCartOpen!(true)}
              className="footer-nav-item footer-nav-button footer-asporto-active"
            >
              <span className="footer-asporto-icon-wrap">
                <svg className="footer-nav-icon" viewBox="0 0 24 24">
                  <path d="M3 3h18v4H3z"/>
                  <path d="M5 7v14h14V7"/>
                  <line x1="9" y1="11" x2="15" y2="11"/>
                </svg>
                {cartCount > 0 && (
                  <span className="footer-asporto-badge">{cartCount}</span>
                )}
              </span>
              Ordine
            </button>
          ) : asportoEnabled && (
            <button
              onClick={handleAsportoClick}
              className={`footer-nav-item footer-nav-button${asportoMode ? ' footer-asporto-active' : ''}`}
            >
              <span className="footer-asporto-icon-wrap">
                <svg className="footer-nav-icon" viewBox="0 0 24 24">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6z"/>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <path d="M16 10a4 4 0 0 1-8 0"/>
                </svg>
                {asportoMode && cartCount > 0 && (
                  <span className="footer-asporto-badge">{cartCount}</span>
                )}
              </span>
              Asporto
            </button>
          )}
        </nav>
        <div className="footer-website">
          <span>
            www.pubfreedom.it è stato realizzato da Nicola Fregola
          </span>
        </div>
      </footer>
      
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        businessInfo={businessInfo}
      />
      
      <CategoriesModal
        isOpen={isCategoriesModalOpen}
        onClose={() => setIsCategoriesModalOpen(false)}
      />

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />

      {/* Pannello ordine tavolo (da QR del tavolo) */}
      {tableUuid && cartOpen && updateCartQty && clearCart && (
        <TableCart
          uuid={tableUuid}
          tableLabel={tableLabel}
          items={cartItems}
          onUpdateQty={updateCartQty}
          onClearCart={clearCart}
          onClose={() => setCartOpen!(false)}
        />
      )}

      {/* Pannello ordine asporto */}
      {!tableUuid && asportoMode && cartOpen && whatsappNumber && updateCartQty && (
        <AsportoCart
          items={cartItems}
          onUpdateQty={updateCartQty}
          onClose={() => setCartOpen!(false)}
          whatsappNumber={whatsappNumber}
          businessName={businessName}
        />
      )}
    </>
  );
};

export default SharedFooter;